import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { ArrowLeft, Plus, Save, Trash2 } from "lucide-react";
import {
  SEGMENT_COLORS,
  SEGMENT_COLOR_TEXT_CLASSES,
} from "@/constants/segmentColors";
import {
  segmentFormSchema,
  type SegmentFormValues,
} from "@/components/spy-dashboard/segments/segmentFormSchema";
interface SegmentFormProps {
  mode: "create" | "edit";
  initialValues: SegmentFormValues;
  isSaving: boolean;
  onBack: () => void;
  onSave: (values: SegmentFormValues) => void;
}

export const SegmentForm = ({
  mode,
  initialValues,
  isSaving,
  onBack,
  onSave,
}: SegmentFormProps) => {
  const form = useForm<SegmentFormValues>({
    resolver: zodResolver(segmentFormSchema),
    defaultValues: initialValues,
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "keywords",
  });

  const targetType = form.watch("target_type");

  const handleSubmit = (values: SegmentFormValues) => {
    onSave(values);
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6 pb-10">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          onClick={onBack}
          className="mt-1 cursor-pointer"
          type="button"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Back
        </Button>
        <h2 className="text-2xl font-semibold">
          {mode === "create" ? "Create New Segment" : "Edit Segment"}
        </h2>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col gap-6">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Basic Info</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel htmlFor="segment-name">Segment Name</FormLabel>
                      <FormControl>
                        <Input
                          id="segment-name"
                          placeholder="e.g. Crypto Casinos"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex gap-4">
                  <FormField
                    control={form.control}
                    name="target_type"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormLabel>Target</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="domain">Domain Level</SelectItem>
                            <SelectItem value="url">URL Level</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="color"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormLabel>Color Label</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {SEGMENT_COLORS.map((color) => (
                              <SelectItem key={color} value={color}>
                                <span
                                  className={`${SEGMENT_COLOR_TEXT_CLASSES[color]} font-medium capitalize`}
                                >
                                  {color}
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Rules Builder</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-6">
                <div className="flex flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Keywords</span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        append({ match: "contains", value: "" })
                      }
                    >
                      <Plus className="mr-1 h-3 w-3" /> Add
                    </Button>
                  </div>

                  {fields.map((field, idx) => (
                    <div key={field.id} className="flex items-center gap-2">
                      <FormField
                        control={form.control}
                        name={`keywords.${idx}.match`}
                        render={({ field: matchField }) => (
                          <FormItem>
                            <Select
                              onValueChange={matchField.onChange}
                              value={matchField.value}
                            >
                              <FormControl>
                                <SelectTrigger className="w-[120px]">
                                  <SelectValue />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="contains">Contains</SelectItem>
                                <SelectItem value="exact">Exact Match</SelectItem>
                              </SelectContent>
                            </Select>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name={`keywords.${idx}.value`}
                        render={({ field: valueField }) => (
                          <FormItem className="flex-1">
                            <FormControl>
                              <Input
                                placeholder="e.g. casino"
                                {...valueField}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => remove(idx)}
                        className="text-destructive hover:bg-[var(--signal-negative-subtle)] hover:text-destructive"
                        aria-label="Remove keyword rule"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}

                  {fields.length === 0 ? (
                    <p className="text-xs text-muted-foreground">
                      No keyword rules added.
                    </p>
                  ) : null}
                </div>

                {targetType === "domain" ? (
                  <FormField
                    control={form.control}
                    name="tlds"
                    render={({ field }) => (
                      <FormItem className="border-t pt-2">
                        <FormLabel htmlFor="segment-tlds">
                          TLDs (comma separated)
                        </FormLabel>
                        <FormControl>
                          <Input
                            id="segment-tlds"
                            placeholder="e.g. de, pl, ro"
                            {...field}
                          />
                        </FormControl>
                        <p className="text-xs text-muted-foreground">
                          Without dots. Example: de, gr, com.br
                        </p>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                ) : (
                  <FormField
                    control={form.control}
                    name="locales"
                    render={({ field }) => (
                      <FormItem className="border-t pt-2">
                        <FormLabel htmlFor="segment-locales">
                          Locales / Paths (comma separated)
                        </FormLabel>
                        <FormControl>
                          <Input
                            id="segment-locales"
                            placeholder="e.g. /de-at/, /fr/"
                            {...field}
                          />
                        </FormControl>
                        <p className="text-xs text-muted-foreground">
                          Will match parts of the URL path.
                        </p>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </CardContent>
            </Card>
          </div>

          <div className="flex justify-end pt-4">
            <Button
              type="submit"
              disabled={isSaving}
              className="cursor-pointer gap-2 px-8"
            >
              <Save className="h-4 w-4" /> Save Segment
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};
